import axios from 'axios';
import debounce from 'lodash/debounce';
import {getFromTemplate, triggerEvent} from '../base/utils';

class TicketAvailability {
    constructor($container) {
        this.$container = $container;

        if (!$container) {
            return;
        }

        this.url = this.$container.dataset.url;
        try {
            this.lang = JSON.parse(this.$container.dataset.lang);
            this.holidays = JSON.parse(this.$container.dataset.holidays);
        } catch (e) {
            console.error('Malformed JSON');
        }

        this.$days = this.$container.querySelector('.js-days');
        this.$date = this.$container.querySelector('.js-date');
        this.$month = this.$container.querySelector('.js-month');
        this.$monthName = this.$container.querySelector('.js-month-name');
        this.$monthNext = this.$container.querySelector('.js-month-next');
        this.$monthPrev = this.$container.querySelector('.js-month-prev');
        this.$categoryTabs = this.$container.querySelector('.js-category-tabs');
        this.$categorySelect = this.$container.querySelector('.js-category-select');
        this.$results = this.$container.querySelector('.js-results');
        this.$eventTemplate = this.$container.querySelector('.js-availability-result');
        this.$emptyTemplate = this.$container.querySelector('.js-availability-empty');
        this.$closedTemplate = this.$container.querySelector('.js-availability-closed');

        if (!this.$days || !this.$date || !this.$month || !this.$monthName || !this.$monthPrev || !this.$monthNext || !this.$results) {
            return;
        }

        this.init();

        window.addEventListener('resize', debounce(this.resize.bind(this), 300));
    }

    init() {
        this.eventTemplate = this.$eventTemplate.innerHTML;
        this.emptyTemplate = this.$emptyTemplate.innerHTML;
        this.closedTemplate = this.$closedTemplate.innerHTML;

        this.initDate();
        this.buildCalendar();
    }

    initCategory() {
        this.resize();

        [...this.$categoryTabsOptions].forEach($el => $el.addEventListener('change', this.changeCategoryTab.bind(this)));
        this.$categorySelect.addEventListener('change', this.changeCategory.bind(this));
    }

    changeCategoryTab(e) {
        this.category = e.target.value;
        this.renderEvents();
    }

    changeCategory() {
        this.category = this.$categorySelect.value;
        this.renderEvents();
    }

    resize() {
        if (window.innerWidth < 1024) {
            [...this.$categoryTabsOptions].forEach($el => $el.setAttribute('disabled', 'disabled'));
            this.$categorySelect.removeAttribute('disabled');
            // this.$categorySelect.value = this.category;
            triggerEvent(this.$categorySelect, 'select', this.category);
        } else {
            [...this.$categoryTabsOptions].forEach($el => $el.removeAttribute('disabled'));
            this.$categorySelect.setAttribute('disabled', 'disabled');
            this.$categoryTabs.querySelector(`[value="${this.category}"]`).setAttribute('checked', 'checked');
        }
    }

    pad(x) {
        return (x < 10 ? '0' : '') + x;
    }

    initDate() {
        let dateNow = new Date();

        this.year = dateNow.getFullYear();
        this.month = dateNow.getMonth() + 1;

        this.setDate();

        this.current = `${this.yearMonthPadded}-${this.pad(dateNow.getDate())}`;
    }

    setDate() {
        this.dateStart = new Date(this.year, this.month - 1, 1);
        this.dateEnd = new Date((new Date(this.year, this.month, 1)).setDate(0));
        this.yearMonth = `${this.year}-${this.month}`;
        this.yearMonthPadded = `${this.year}-${this.pad(this.month)}`;
    }

    buildCalendar() {
        this.showMonth();

        this.$monthNext.addEventListener('click', this.nextMonth.bind(this));
        this.$monthPrev.addEventListener('click', this.prevMonth.bind(this));
    }

    changeDay(e) {
        this.$days.querySelector('.selected').classList.remove('selected');
        e.target.classList.add('selected');
        this.current = e.target.dataset.date;
        this.$date.innerHTML = e.target.title;
        this.renderEvents();
    }

    prevMonth() {
        if (this.month === 0) {
            this.year--;
            this.month = 11;
        } else {
            this.month = (this.month - 1) % 12;
        }

        this.setDate();
        this.current = `${this.yearMonthPadded}-${this.dateEnd.getDate()}`;

        this.showMonth();

        if (this.dateStart <= Date.now()) {
            this.$monthPrev.setAttribute('disabled', 'disabled');
        }
    }

    nextMonth() {
        this.month = (this.month + 1) % 12;
        if (this.month === 0) this.year++;

        this.setDate();
        this.current = `${this.yearMonthPadded}-01`;

        this.showMonth();

        if (this.dateStart > Date.now()) {
            this.$monthPrev.removeAttribute('disabled');
        }
    }

    showMonth() {
        let firstWeekday = this.dateStart.getDay(),
            lastDay = this.dateEnd.getDate(),
            dateParam = `${this.yearMonth}-01/${this.yearMonth}-${lastDay}`;

        this.$results.classList.add('loader');
        this.renderCalendar(firstWeekday, lastDay);
        this.getEvents(dateParam);
    }

    renderCalendar(firstWeekday, lastDay) {
        let html = '',
            monthName = this.lang.monthNames[this.month];

        for (let d = firstWeekday - 1; d > 0; d--) {
            html += '<li class="day-previous-month"></li>';
        }

        for (let d = 1, weekday = firstWeekday; d <= lastDay; d++) {
            let day = this.pad(d),
                isSelected = `${this.yearMonthPadded}-${day}` === this.current;

            html += `<li><div>${this.lang.weekdays[weekday]}</div><button class="day ${isSelected ? 'selected' : ''}" data-date="${this.yearMonthPadded}-${day}" title="${d} ${monthName}">${d}</button></li>`;
            weekday = (weekday + 1) % 7;

            if (isSelected) {
                this.$date.innerHTML = `${d} ${monthName}`;
            }
        }

        this.$days.innerHTML = '';
        this.$days.insertAdjacentHTML('beforeend', html);

        this.$monthName.innerHTML = monthName;

        [...this.$days.querySelectorAll('.day')].forEach($el => $el.addEventListener('click', this.changeDay.bind(this)));
    }

    getEvents(dateParam) {
        axios.get(this.url)
            .then(data => {
                this.$results.classList.remove('loader');
                if (data.status === 200) {
                    this.data = data.data;
                    if (!this.categories) {
                        this.renderCategories();
                    }
                    this.renderEvents();
                }
            })
            .catch((error) => {
                console.log(error);
                this.$results.innerHTML = '';
                this.$results.classList.remove('loader');
                // this.$results.appendChild(this.getFromTemplate(this.errorTemplate));
            });
    }

    renderCategories() {
        let tabs = '',
            options = [];

        this.categories = this.data['event-collections'];

        for (let c in this.categories) {
            let name = this.categories[c];

            if (!this.category) this.category = c;

            tabs += `<li class="tabs-list-item" role="presentation">
                <input type="radio" name="category" value="${c}" id="${c}" aria-controls="availability-results" />
                <label class="tabs-tab" for="${c}">${name}</label>
            </li>`;

            // options += `<option value="${c}" >${name}</option>`;
            options.push({value: c, label: name});
        }

        triggerEvent(this.$categorySelect, 'set', options);

        this.$categoryTabs.insertAdjacentHTML('beforeend', tabs);
        // this.$categorySelect.insertAdjacentHTML('beforeend', options);
        this.$categoryTabsOptions = this.$categoryTabs.querySelectorAll('input');

        this.initCategory();
    }

    renderEvents() {
        let dayEvents = this.data[this.current],
            events = dayEvents ? dayEvents[this.category] : null;

        this.$results.innerHTML = '';

        let holidayMsg = this.isHoliday(this.current, this.category);
        if (holidayMsg) {
            this.$results.innerHTML = `<div class="availability-results-empty">${holidayMsg}</div>`;
            return;
        }

        if (!dayEvents) {
            this.$results.innerHTML = '<div class="availability-results-empty">' + this.closedTemplate + '</div>';
            return;
        }

        if (!events) {
            this.$results.innerHTML = '<div class="availability-results-empty">' + this.emptyTemplate + '</div>';
            return;
        }

        for (let e in events) {
            if (events.hasOwnProperty(e)) {
                let event = events[e];

                for (let hour in event.availability) {
                    if (event.availability.hasOwnProperty(hour)) {
                        this.$results.appendChild(this.renderEvent(event.attractionEventName, hour, event.availability[hour].quantity, this.category, event.attractionEventWww));
                    }
                }
            }
        }
    }

    renderEvent(name, hour, quantity, category, url) {
        let itemHtml = this.eventTemplate,
            $item;

        itemHtml = itemHtml.replace(`[hour]`, hour);
        itemHtml = itemHtml.replace(`[quantity]`, quantity);
        if (url) {
            name = `<a href="${url}" class="link" target="_blank" rel="noopener"><span class="underline">${name}</span></a>`;
        }
        itemHtml = itemHtml.replace(`[name]`, name);
        itemHtml = itemHtml.replace(/\[label]/gi, this.lang.labels[quantity]);

        $item = getFromTemplate(itemHtml);

        if (category === 'label_cnk_attraction_event_collection_planetarium') {
            $item.classList.add('availability-result-pla');
        } else if (category === 'label_cnk_attraction_event_collection_laboratory_group') {
            $item.classList.add('availability-result-lab');
        } else {
            $item.querySelector('.name').remove();
        }

        return $item;
    }

    isHoliday(date, category) {
        if (this.holidays.length === 0) return false;

        let i = 0,
            n = this.holidays.length,
            isHoliday = false;

        while (!isHoliday && i < n) {
            let holiday = this.holidays[i];

            isHoliday = (category === holiday.category && date >= holiday.from && date <= holiday.to) ? holiday.message : false;
            i++;
        }

        return isHoliday;
    }
}

export default TicketAvailability;
